import { css, keyframes } from '@emotion/react'
import {
    ButtonHTMLAttributes,
    memo,
    useCallback,
    useImperativeHandle,
    useRef,
} from 'react'
import { getThemeSpacing } from '../styles/appTheme'
import { palette } from '../styles/colors'
import layers from '../styles/layers'
import shape from '../styles/shape'

const pulse = keyframes({
    '0%': {
        transform: 'scale(0.95)',
        opacity: 0.35,
    },
    '50%': {
        transform: 'scale(1.05)',
        opacity: 0.25,
    },
    '100%': {
        transform: 'scale(0.95)',
        opacity: 0.35,
    },
})

const buttonStyles = css({
    margin: 0,
    padding: 0,
    outline: 0,
    backgroundColor: palette.greenMFC,
    color: palette.commonWhite,
    cursor: 'pointer',
    // width: '100%',
    overflow: 'hidden',
    position: 'relative',
    fontWeight: 600,
    fontFamily: 'inherit',
    // fontSize: '0.875rem',
    lineHeight: 1.75,
    letterSpacing: '0.02857em',
    textTransform: 'capitalize',
    WebkitFontSmoothing: 'antialiased',
    WebkitTapHighlightColor: 'transparent',
    transition:
        'background-color 0.16s ease, box-shadow 0.16s ease, color 0.16s ease',
    border: '1px solid ' + palette.buttonPrimaryBorder,
    borderRadius: shape.borderRadius,
    '&:focus-visible span:last-of-type': {
        width: '80%',
        aspectRatio: 1,
        left: '50% !important',
        top: '50% !important',
        translate: '-50% -50%',
        animation: `${pulse} 2s ease-in-out infinite`,
    },
    '@media (hover: none)': {
        '&:hover': {
            background: palette.greenMFC,
        },
    },
    '&:hover': {
        backgroundColor: palette.greenMFCDark,
    },
    '&:disabled': {
        backgroundColor: palette.disabledGrey,
        color: palette.disabledText,
        fontWeight: 500,
        pointerEvents: 'none',
    },
    '& svg': {
        color: palette.contrastIcons,
    },
})

const elevationStyles = css({
    '&:hover': {
        boxShadow: layers.one,
    },
})

const rippleStyles = css({
    position: 'absolute',
    opacity: 0.5,
    background: palette.commonWhite,
    transform: 'translate(-50%, -50%)',
    pointerEvents: 'none',
    borderRadius: '50%',
    aspectRatio: 1,
    WebkitTapHighlightColor: 'transparent',
})

const cancelStyles = css({
    background: palette.greyMFC,
    color: palette.commonBlack,
    '&:hover': {
        color: palette.contrastText,
    },
    '@media (hover: none)': {
        '&:hover': {
            background: palette.greyMFC,
        },
    },
})

const cleanStyles = css({
    color: palette.contrastText,
    background: 'transparent',
    border: '1px solid transparent',
    padding: `${getThemeSpacing(0.5)} ${getThemeSpacing(1.5)}`,
    fontWeight: 500,
    '& span:last-of-type': {
        background: palette.active,
    },
    '&:hover': {
        backgroundColor: palette.activeHover,
    },
    '@media (hover: none)': {
        '&:hover': {
            background: 'transparent',
        },
    },
})

const borderStyles = css({
    border: '1px solid ' + palette.buttonBorder,
})

const smallStyles = css({
    fontSize: '0.8125rem',
})

const mediumStyles = css({
    minWidth: 64,
    fontSize: '0.875rem',
    padding: `${getThemeSpacing(1)} ${getThemeSpacing(1.5)}`,
})

const largeStyles = css({
    fontSize: '0.875rem',
    minWidth: 64,
    width: '100%',
    paddingTop: getThemeSpacing(2),
    paddingBottom: getThemeSpacing(2),
})

const getSizes = (size: ButtonSize) => {
    switch (size) {
        default:
        case 'small':
            return smallStyles
        case 'medium':
            return mediumStyles
        case 'large':
            return largeStyles
    }
}

const animateRipple = (clientX, clientY, rippleRef, buttonRef) => {
    const rect = buttonRef.current.getBoundingClientRect()

    const ripplePositionX = clientX - rect.x
    const ripplePositionY = clientY - rect.y
    const width = rect.width * 1.75
    const rippleAnimation = [
        { opacity: 0.5, width: 0, height: 0 },
        {
            opacity: 0,
            width: `${width}px`,
            height: `${width}px`,
        },
    ]

    rippleRef.current.animate?.(rippleAnimation, {
        duration: 380,
        iterations: 1,
    })
    rippleRef.current.style.left = ripplePositionX + 'px'
    rippleRef.current.style.top = ripplePositionY + 'px'
}

export type ButtonSize = 'small' | 'medium' | 'large'

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
    ref?: any
    children?: any
    size?: ButtonSize
    clean?: boolean
    border?: boolean
    cancel?: boolean
    disabled?: boolean
    disableElevation?: boolean
}

const Button = (props: ButtonProps) => {
    const {
        ref,
        children,
        className,
        size = 'small',
        border = false,
        clean = false,
        cancel = false,
        disableElevation = false,
        onClick,
        onTouchStart,
        ...rest
    } = props

    const rippleRef = useRef<HTMLSpanElement>(null)
    const buttonRef = useRef<HTMLButtonElement>(null)
    useImperativeHandle(ref, () => buttonRef.current)

    const sizeStyles = getSizes(size)

    const rippleClick = useCallback(
        (event) => {
            animateRipple(event.clientX, event.clientY, rippleRef, buttonRef)
            onClick?.(event)
        },
        [onClick, onTouchStart],
    )

    const rippleTouch = useCallback(
        (event) => {
            if (onTouchStart) {
                animateRipple(
                    event.touches[0].clientX,
                    event.touches[0].clientY,
                    rippleRef,
                    buttonRef,
                )
                onTouchStart(event)
            }
        },
        [onTouchStart],
    )
    return (
        <button
            ref={buttonRef}
            onClick={rippleClick}
            onTouchStart={rippleTouch}
            className={`mfc-button ${className ?? ''}`}
            css={[
                buttonStyles,
                disableElevation || clean || cancel ? null : elevationStyles,
                clean || cancel ? cleanStyles : null,
                sizeStyles,
                cancel ? cancelStyles : null,
                border ? borderStyles : null,
            ]}
            {...rest}
        >
            {children}
            <span ref={rippleRef} css={rippleStyles}></span>
        </button>
    )
}

export default memo(Button)
